import color from '@haaretz/l-color.macro';
import merge from '@haaretz/l-merge.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import zIndex from '@haaretz/l-z-index.macro';
import Icon from '@haaretz/s-icon';
import * as React from 'react';
import s9 from 'style9';

import type { StepDataFragment } from '@haaretz/s-fragments/StepData';
const linearGradient = `linear-gradient(270deg, ${color('primary900')} 33.3%, ${color(
  'progressBarCircle'
)} 100%)` as string;

// `c` is short for `classNames`
const c = s9.create({
  base: {
    display: 'inline-flex',
    columnGap: space(4),
    position: 'relative',
  },
  iconWrapper: {
    backgroundColor: color('progressBarCircle'),
    borderRadius: radius('circle'),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: space(0.75),
    paddingBottom: space(0.75),
    paddingRight: space(0.75),
    paddingLeft: space(0.75),
    width: space(4),
    aspectRatio: '1',
    zIndex: zIndex('above'),

    ...merge(
      mq({
        from: 'l',
        value: {
          width: space(5),
        },
      })
    ),
  },
  check: {
    fontSize: space(2.5),
    color: color('neutral100'),

    ...merge(
      mq({
        from: 'l',
        value: {
          fontSize: space(3),
        },
      })
    ),
  },
  filled: {
    backgroundColor: color('primary1000'),
  },
  line: {
    height: space(1),
    width: '100%',
    position: 'absolute',
    top: '50%',
    insetInlineEnd: 0,
    transform: 'translateY(-50%)',
    backgroundImage: linearGradient,
  },
});

export type StepName = StepDataFragment['name'];

export interface StepProps {
  currentStep: StepName;
  allSteps: StepDataFragment[];
}

export default function Step({ currentStep, allSteps }: StepProps) {
  const stepNames = allSteps.map(step => step.name);
  const currentStepIndex = stepNames.indexOf(currentStep);

  if (currentStepIndex === -1) return null;

  return (
    <div className={s9(c.base)}>
      <div className={s9(c.line)} />
      {stepNames.map((name, i) => {
        const shouldBeFilled = i <= currentStepIndex;
        const shouldBeChecked = i < currentStepIndex || currentStepIndex === stepNames.length - 1;

        return (
          <div
            key={name}
            className={s9(c.iconWrapper, shouldBeFilled && c.filled)}
            data-testid={shouldBeFilled ? 'filled-step' : 'step'}
          >
            {shouldBeChecked ? (
              <Icon icon="check" styleExtend={[c.check]} data-testid="checked-step" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
